<template>
  <div>
    <data-table
      ref="dataTableRef"
      :config="dataTableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';
import { comparativoFranquiasExplanations } from './explanations';

interface DataTableInstance {
  loadData: () => void;
}

// Get i18n composable
const { t } = useI18n();
const EX = comparativoFranquiasExplanations(t);
const { initialized } = useFiltro();

// Reactive data
const dataTableRef = ref<DataTableInstance | null>(null);
const dataLoaded = ref<boolean>(false);

// Computed properties
const endpoint = computed(() => '/index.php/relatorios/comparativo_franquias/buscaDadosTabela');

const tableColumns = computed(() => [
  {
    data: 'franquia_id',
    title: 'ID',
    width: 80,
    sortable: true
  },
  {
    data: 'franquia',
    title: t('Franquia'),
    width: 150,
    sortable: true
  },
  {
    data: 'numero_orcamentos',
    title: t('Orçamentos'),
    width: 120,
    sortable: true,
    footerType: 'sum'
  },
  {
    data: 'valor_orcamentos',
    title: t('Total em orçamentos'),
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'sum'
  },
  {
    data: 'entradas',
    title: t('Receita'),
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'sum'
  },
  {
    data: 'falta_faturar',
    title: t('Falta Faturar'),
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'sum'
  },
  {
    data: 'previsao_faturamento',
    title: t('Previsão'),
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'sum'
  },
  {
    data: 'numero_clientes',
    title: t('Clientes'),
    width: 100,
    sortable: true,
    footerType: 'sum'
  },
  {
    data: 'ticket_medio',
    title: t('Ticket médio'),
    width: 120,
    sortable: true,
    renderType: 'currency',
    footerType: 'avg'
  },
  {
    data: 'compromissos_total',
    title: t('Compromissos'),
    width: 130,
    sortable: true,
    footerType: 'sum'
  },
  {
    data: 'compromissos_realizados',
    title: t('Compromissos realizados'),
    width: 180,
    sortable: true,
    footerType: 'sum'
  },
  {
    data: 'despesas',
    title: t('Despesas'),
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'sum'
  },
  // Chamado 2026-0025: métricas que a API já entregava; mostradas desde o DS 2026-0082.
  {
    data: 'orcamentos_aprovados',
    title: t('Orçamentos aprovados'),
    width: 150,
    sortable: true,
    renderType: 'number',
    footerType: 'sum',
    explanation: EX.orcamentos_aprovados
  },
  {
    data: 'recebimentos',
    title: t('Recebimentos'),
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'sum',
    explanation: EX.recebimentos
  },
  {
    data: 'media_itens_orcamento',
    title: t('Média de itens por orçamento'),
    width: 150,
    sortable: true,
    renderType: 'number',
    explanation: EX.media_itens_orcamento
  },
  {
    data: 'cac',
    title: t('CAC'),
    width: 120,
    sortable: true,
    renderType: 'currency',
    explanation: EX.cac
  },
  {
    data: 'conversao_orcamentos',
    title: t('Aprovação de orçamentos (%)'),
    width: 160,
    sortable: true,
    renderType: 'percentage',
    explanation: EX.conversao_orcamentos
  },
  {
    data: 'conversao_realizacao',
    title: t('Conversão em realizados (%)'),
    width: 160,
    sortable: true,
    renderType: 'percentage',
    explanation: EX.conversao_realizacao
  },
  {
    data: 'iao',
    title: t('IAO (%)'),
    width: 100,
    sortable: true,
    renderType: 'percentage',
    explanation: EX.iao
  }
]);

const dataTableConfig = computed(() => {
  return {
    tableId: 'tabelaComparativoFranquias',
    filename: `comparativo_franquias_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 100,
    pagination: true,
    footerLabel: t('Totais'),
    rowSelection: 'single',
    resizableColumns: true,
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const filterConfig = computed(() => {
  return {
    searchName: t('Procurar'),
    choosePlaceholder: t('Escolha'),
    refreshName: t('Atualizar'),
    printName: t('Imprimir'),
    debug: false,
    columns: {
      'franquia_id': 'text',
      'franquia': 'text',
      'numero_orcamentos': 'range',
      'valor_orcamentos': 'range',
      'entradas': 'range',
      'falta_faturar': 'range',
      'previsao_faturamento': 'range',
      'numero_clientes': 'range',
      'ticket_medio': 'range',
      'compromissos_total': 'range',
      'compromissos_realizados': 'range',
      'despesas': 'range',
      'orcamentos_aprovados': 'range',
      'recebimentos': 'range',
      'media_itens_orcamento': 'range',
      'cac': 'range',
      'conversao_orcamentos': 'range',
      'conversao_realizacao': 'range',
      'iao': 'range'
    }
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV'),
    loading: false
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

// Methods
const loadData = () => {
  const stack = new Error().stack;
  log('ComparativoFranquiasTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (dataTableRef.value) {
    dataTableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('ComparativoFranquiasTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  dataLoaded.value = true;
  eventBus.emit('globalMessage.success', t(`Carregados`) + ` ${data.length} ` + t(`registros do comparativo de franquias`));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('ComparativoFranquiasTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : error.message || '';
  eventBus.emit('globalMessage.error', t('Erro ao carregar comparativo de franquias: ') + message);
};

// Named handler for proper cleanup
const onFilterUpdated = (): void => {
  log('ComparativoFranquiasTable', 'event:filter.updated', 'Received, reloading data');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('ComparativoFranquiasTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  // Don't load data on mount - let DataTable handle initial load via autoLoad
  log('ComparativoFranquiasTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  eventBus.on('filter.updated', onFilterUpdated);
});

onBeforeUnmount(() => {
  log('ComparativoFranquiasTable', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('filter.updated', onFilterUpdated);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
</style>
